<template>
  <div class="success-container">
    <!-- Loading State -->
    <div v-if="loading" class="processing-state">
      <div class="processing-animation">
        <svg class="circular-progress" viewBox="0 0 50 50">
          <circle
            class="path"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke-width="4"
          ></circle>
        </svg>
      </div>
      <p class="processing-text">{{ processingText }}</p>
    </div>
    
    <!-- Success State -->
    <div v-else class="success-content">
      <!-- Success Icon -->
      <div class="success-icon-wrapper">
        <svg class="success-icon" viewBox="0 0 52 52">
          <circle class="success-circle" cx="26" cy="26" r="25" fill="none" />
          <path class="success-check" d="M14.1 27.2l7.1 7.2 16.7-16.8" fill="none" />
        </svg>
      </div>
      
      <!-- Success Message -->
      <h2 class="success-title">{{ title }}</h2>
      <p class="success-message">{{ message }}</p>
      
      <!-- Reference Number -->
      <div v-if="referenceNumber" class="reference-number">
        <p class="reference-label">Reference Number</p>
        <p class="reference-value">{{ referenceNumber }}</p>
        <button 
          @click="copyReferenceNumber" 
          class="copy-button"
          :class="{ 'copied': copied }"
        >
          <Icon :icon="copied ? 'mdi:check' : 'mdi:content-copy'" />
          {{ copied ? 'Copied' : 'Copy' }}
        </button>
      </div>
      
      <!-- Details Section -->
      <div v-if="details" class="details-section">
        <div v-for="(detail, index) in details" :key="index" class="detail-item">
          <span class="detail-label">{{ detail.label }}:</span>
          <span class="detail-value">{{ detail.value }}</span>
        </div>
      </div>
      
      <!-- Next Steps -->
      <div v-if="nextSteps && nextSteps.length" class="next-steps">
        <h3 class="next-steps-title">Next Steps</h3>
        <ul class="steps-list">
          <li v-for="(step, index) in nextSteps" :key="index" class="step-item">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-content">{{ step }}</div>
          </li>
        </ul>
      </div>
      
      <!-- Action Buttons -->
      <div class="action-buttons">
        <slot name="actions">
          <button 
            v-if="primaryAction" 
            @click="$emit('primary-action')" 
            class="primary-action-button"
          >
            {{ primaryAction }}
          </button>
          <button 
            v-if="secondaryAction" 
            @click="$emit('secondary-action')" 
            class="secondary-action-button"
          >
            {{ secondaryAction }}
          </button>
        </slot>
      </div>
      
      <!-- Email Receipt -->
      <div v-if="enableEmailReceipt" class="email-receipt">
        <div class="email-receipt-form" :class="{ 'email-sent': emailSent }">
          <template v-if="!emailSent">
            <label for="receipt-email" class="email-label">
              Send a receipt to your email
            </label>
            <div class="email-input-group">
              <input
                id="receipt-email"
                v-model="emailInput"
                type="email"
                class="email-input"
                placeholder="Enter your email address"
                :disabled="sendingEmail"
              />
              <button 
                @click="sendEmailReceipt" 
                class="send-email-button"
                :disabled="!isValidEmail || sendingEmail"
              >
                <span v-if="sendingEmail">
                  <Icon icon="mdi:loading" class="spin-icon" />
                </span>
                <span v-else>Send</span>
              </button>
            </div>
            <p v-if="emailError" class="email-error">
              {{ emailError }}
            </p>
          </template>
          <div v-else class="email-sent-message">
            <Icon icon="mdi:email-check" class="email-sent-icon" />
            Receipt sent to {{ emailInput }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  processingText: {
    type: String,
    default: 'Processing your request...'
  },
  title: {
    type: String,
    default: 'Success!'
  },
  message: {
    type: String,
    default: 'Your request has been successfully submitted.'
  },
  referenceNumber: {
    type: String,
    default: ''
  },
  details: {
    type: Array,
    default: () => []
  },
  nextSteps: {
    type: Array,
    default: () => []
  },
  primaryAction: {
    type: String,
    default: 'Return to Dashboard'
  },
  secondaryAction: {
    type: String,
    default: ''
  },
  enableEmailReceipt: {
    type: Boolean,
    default: false
  },
  defaultEmail: {
    type: String,
    default: ''
  }
});

const emit = defineEmits([
  'primary-action',
  'secondary-action',
  'email-sent'
]);

// Copy functionality
const copied = ref(false);
const emailInput = ref(props.defaultEmail);
const emailSent = ref(false);
const emailError = ref('');
const sendingEmail = ref(false);

// Validate email format
const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailInput.value);
});

// Copy reference number to clipboard
const copyReferenceNumber = () => {
  if (props.referenceNumber) {
    navigator.clipboard.writeText(props.referenceNumber).then(() => {
      copied.value = true;
      setTimeout(() => {
        copied.value = false;
      }, 2000);
    });
  }
};

// Send email receipt
const sendEmailReceipt = async () => {
  if (!isValidEmail.value) {
    emailError.value = 'Please enter a valid email address';
    return;
  }
  
  sendingEmail.value = true;
  emailError.value = '';
  
  try {
    // Make a real API call to send the receipt
    const response = await fetch('/api/send-receipt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email: emailInput.value,
        reference_number: props.referenceNumber,
        details: props.details
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send receipt');
    }
    
    sendingEmail.value = false;
    emailSent.value = true;
    emit('email-sent', emailInput.value);
  } catch (error) {
    console.error('Error sending receipt:', error);
    sendingEmail.value = false;
    emailError.value = 'Failed to send receipt. Please try again.';
  }
};

// Initialize component with animation delay
onMounted(() => {
  // Add any initialization code here
});
</script>

<style scoped>
.success-container {
  @apply max-w-lg w-full mx-auto p-5 md:p-8 bg-white rounded-lg shadow-md;
}

.processing-state {
  @apply flex flex-col items-center justify-center py-12;
}

.processing-animation {
  @apply w-16 h-16 mb-4;
}

.processing-text {
  @apply text-gray-600 text-center;
}

.circular-progress {
  animation: rotate 2s linear infinite;
  transform-origin: center center;
  width: 100%;
  height: 100%;
  margin: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.path {
  stroke: #38a169;
  stroke-dasharray: 89, 200;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.success-content {
  @apply flex flex-col items-center;
}

.success-icon-wrapper {
  @apply mb-6 w-20 h-20 md:w-24 md:h-24;
}

.success-icon {
  width: 100%;
  height: 100%;
}

.success-circle {
  stroke: #38a169;
  stroke-width: 2;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.success-check {
  stroke: #38a169;
  stroke-width: 2;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.6s forwards;
}

.success-title {
  @apply text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center;
}

.success-message {
  @apply text-base md:text-lg text-gray-600 text-center mb-6;
}

.reference-number {
  @apply w-full max-w-md bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200;
}

.reference-label {
  @apply text-sm text-gray-500 mb-1;
}

.reference-value {
  @apply text-lg font-medium text-gray-800 mb-2 break-all;
}

.copy-button {
  @apply inline-flex items-center text-sm bg-white border border-gray-300 rounded-md px-3 py-1 text-gray-600 hover:bg-gray-50 transition-colors;
}

.copy-button.copied {
  @apply bg-green-50 text-green-700 border-green-200;
}

.copy-button svg {
  @apply mr-1;
}

.details-section {
  @apply w-full border-t border-b border-gray-200 py-4 mb-6;
}

.detail-item {
  @apply flex justify-between py-2;
}

.detail-label {
  @apply text-gray-600;
}

.detail-value {
  @apply text-gray-900 font-medium;
}

.next-steps {
  @apply w-full mb-6;
}

.next-steps-title {
  @apply text-lg font-medium text-gray-800 mb-3;
}

.steps-list {
  @apply space-y-3;
}

.step-item {
  @apply flex items-start;
}

.step-number {
  @apply flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center mr-3 font-medium;
}

.step-content {
  @apply text-gray-700 pt-0.5;
}

.action-buttons {
  @apply flex flex-col sm:flex-row gap-3 w-full mb-6;
}

.primary-action-button {
  @apply w-full sm:flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors;
}

.secondary-action-button {
  @apply w-full sm:flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors;
}

.email-receipt {
  @apply w-full mt-4;
}

.email-receipt-form {
  @apply rounded-md border border-gray-200 p-4;
}

.email-label {
  @apply block text-sm font-medium text-gray-700 mb-2;
}

.email-input-group {
  @apply flex;
}

.email-input {
  @apply flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500;
}

.send-email-button {
  @apply bg-primary-600 text-white px-4 py-2 rounded-r-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed;
}

.email-error {
  @apply text-red-500 text-sm mt-1;
}

.email-sent-message {
  @apply text-green-600 flex items-center;
}

.email-sent-icon {
  @apply mr-2 text-green-500;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 